import { useQuery } from '@tanstack/react-query';
import { getPermissions } from '@/api/users';

/**
 * Hook to fetch the current user's permissions from the backend.
 *
 * IMPORTANT: All permission logic is driven by the backend. Use the returned
 * values to show/hide/enable UI - DO NOT implement frontend-based permission
 * checks based on the user's role.
 *
 * The ['user-permissions'] query key is invalidated by user-management
 * mutations, so permission changes take effect without a reload.
 */
export const usePermissions = () => {
  return useQuery({
    queryKey: ['user-permissions'],
    queryFn: getPermissions,
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error) => {
      // Don't retry on 403 (no role) or 401 (not authenticated)
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 403 || status === 401) {
        return false;
      }
      return failureCount < 3;
    },
  });
};
